import React, { useState, useEffect, useRef, useCallback } from "react";
import "./App.css";
import { initialRecords, appThemes } from "./mock";
import AuthScreen from "./components/AuthScreen";
import Header from "./components/Header";
import HomeView from "./components/HomeView";
import FormView from "./components/FormView";
import ListView from "./components/ListView";
import SettingsView from "./components/SettingsView";
import { BottomSearch, Footer, SearchResultModal, CustomModal } from "./components/Overlays";
import { useVoiceEngine } from "./hooks/useVoiceEngine";

const LS = {
  records: "ragip_pasa_korusu_records",
  logo: "ragip_pasa_korusu_logo",
  password: "ragip_pasa_korusu_password",
  phone: "ragip_pasa_korusu_phone",
  theme: "ragip_pasa_korusu_theme",
};

const formFieldsOrder = ["fullName", "plateNo", "carModel", "relationDegree", "aptNo", "phoneNo", "infoNote"];

export default function App() {
  const [records, setRecords] = useState(() => {
    try {
      const s = localStorage.getItem(LS.records);
      return s ? JSON.parse(s) : initialRecords;
    } catch (e) {
      return initialRecords;
    }
  });
  const [logo, setLogo] = useState(() => localStorage.getItem(LS.logo) || "");
  const [password, setPassword] = useState(() => localStorage.getItem(LS.password) || "");
  const [phone, setPhone] = useState(() => localStorage.getItem(LS.phone) || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authView, setAuthView] = useState(null);
  const [view, setView] = useState("home");
  const [activeField, setActiveFieldState] = useState("globalSearchInput");
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [voiceSupported, setVoiceSupported] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchModal, setSearchModal] = useState({ open: false, results: [], query: "", subtitle: "" });
  const [customModal, setCustomModal] = useState({
    open: false,
    type: "info",
    title: "",
    desc: "",
    confirmText: "Tamam",
    isConfirm: false,
    goHome: false,
    onConfirm: null,
  });
  const [noteListeningId, setNoteListeningId] = useState(null);
  const [footerKey, setFooterKey] = useState(0);

  const activeFieldRef = useRef(activeField);
  const waitingNoteRef = useRef(false);
  const currentViewedRef = useRef(null);
  const fieldSettersRef = useRef({});
  const formApiRef = useRef(null);
  const settingsApiRef = useRef(null);
  const searchModalApiRef = useRef(null);
  const stateRef = useRef({});
  const customModalRef = useRef(customModal);

  activeFieldRef.current = activeField;
  customModalRef.current = customModal;
  stateRef.current = {
    view,
    searchOpen: searchModal.open,
    customOpen: customModal.open,
    customIsConfirm: customModal.isConfirm,
    isAuthenticated,
  };

  // ---------- Yardımcılar ----------

  const persistRecords = useCallback((recs) => {
    setRecords(recs);
    localStorage.setItem(LS.records, JSON.stringify(recs));
  }, []);

  const setActiveField = useCallback((id) => {
    setActiveFieldState(id);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.focus();
    }, 40);
  }, []);

  const switchView = useCallback(
    (v) => {
      setView(v);
      if (v === "form") setActiveField("fullName");
      else if (v === "list") setActiveField("globalSearchInput");
    },
    [setActiveField]
  );

  const notify = useCallback((title, desc, type = "info", goHomeOnClose = false) => {
    setCustomModal({
      open: true,
      type,
      title,
      desc,
      confirmText: "Tamam",
      isConfirm: false,
      goHome: goHomeOnClose,
      onConfirm: null,
    });
  }, []);

  const closeCustomModal = useCallback(() => {
    setCustomModal((m) => ({ ...m, open: false, isConfirm: false }));
  }, []);

  const handleCustomConfirm = useCallback(() => {
    const m = customModalRef.current;
    closeCustomModal();
    if (m.onConfirm) {
      m.onConfirm();
      return;
    }
    if (m.goHome) switchView("home");
  }, [closeCustomModal, switchView]);

  const applyTheme = useCallback(
    (name, showNotify = true) => {
      const t = appThemes[name];
      if (!t) return;
      const r = document.documentElement.style;
      r.setProperty("--color-brand-50", t[50]);
      r.setProperty("--color-brand-500", t[500]);
      r.setProperty("--color-brand-600", t[600]);
      r.setProperty("--color-brand-700", t[700]);
      localStorage.setItem(LS.theme, name);
      if (showNotify) notify("Tema Güncellendi", "Uygulama tema rengi başarıyla değiştirildi.", "success", false);
    },
    [notify]
  );

  const registerSetters = useCallback((map) => {
    fieldSettersRef.current = { ...fieldSettersRef.current, ...map };
  }, []);

  // ---------- Giriş ----------

  const loginSuccess = useCallback(() => {
    setIsAuthenticated(true);
    setAuthView(null);
    setFooterKey((k) => k + 1);
  }, []);

  useEffect(() => {
    applyTheme(localStorage.getItem(LS.theme) || "green", false);
    const pw = localStorage.getItem(LS.password) || "";
    if (!pw) loginSuccess();
    else setAuthView("login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const iv = setInterval(() => setFooterKey((k) => k + 1), 60000);
    return () => clearInterval(iv);
  }, [isAuthenticated]);

  const handleLogin = (p) => {
    if (p === password) {
      loginSuccess();
      return true;
    }
    return false;
  };

  const verifyPhone = (p) => p === phone;

  const handlePasswordReset = (np) => {
    setPassword(np);
    localStorage.setItem(LS.password, np);
    notify("Başarılı", "Şifreniz başarıyla yenilendi. Yeni şifrenizle giriş yapıyorsunuz.", "success", false);
    loginSuccess();
  };

  // ---------- Ayarlar ----------

  const verifyOldPassword = (old) => old === password;

  const handleChangePassword = (np) => {
    setPassword(np);
    localStorage.setItem(LS.password, np);
    notify("Başarılı", "Uygulama şifreniz başarıyla güncellendi.", "success", false);
  };

  const handleCreatePassword = (ph, np) => {
    setPhone(ph);
    setPassword(np);
    localStorage.setItem(LS.phone, ph);
    localStorage.setItem(LS.password, np);
    notify("Başarılı", "Telefon numaranız doğrulandı ve uygulamanız şifrelendi.", "success", false);
  };

  // ---------- Kayıt İşlemleri ----------

  const saveRecord = (data) => {
    const newRecord = { id: Date.now(), ...data };
    persistRecords([newRecord, ...records]);
    notify("Başarılı", "Kayıt başarıyla oluşturuldu.", "success", true);
  };

  const deleteRecord = useCallback(
    (id, fromSearch = false) => {
      persistRecords(records.filter((r) => r.id !== id));
      if (fromSearch) {
        closeSearchModal();
        notify("Başarılı", "Kayıt başarıyla silindi.", "success", true);
      } else {
        notify("Başarılı", "Kayıt başarıyla silindi.", "success", false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [records, persistRecords, notify]
  );

  const confirmDeleteAll = () => {
    if (records.length === 0) {
      notify("Bilgi", "Silinecek kayıt bulunmuyor.", "info", false);
      return;
    }
    setCustomModal({
      open: true,
      type: "confirm",
      title: "Tüm Kayıtları Sil",
      desc: "Kayıtlı tüm kişileri tamamen silmek istediğinize emin misiniz?",
      confirmText: "Hepsini Sil",
      isConfirm: true,
      goHome: false,
      onConfirm: () => {
        persistRecords([]);
        notify("Başarılı", "Tüm kayıtlar silindi.", "success", true);
      },
    });
  };

  const copyToClipboard = (text) => {
    const done = () => notify("Kopyalandı", `Telefon numarası (${text}) panoya kopyalandı.`, "success", false);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      const t = document.createElement("textarea");
      t.value = text;
      document.body.appendChild(t);
      t.select();
      document.execCommand("copy");
      document.body.removeChild(t);
      done();
    }
  };

  // ---------- Arama ----------

  const executeGlobalSearch = useCallback(
    (qParam) => {
      const q = (qParam !== undefined ? qParam : searchQuery).trim();
      if (q.length === 0) return;
      const filtered = records.filter(
        (item) =>
          item.fullName.toLowerCase().includes(q.toLowerCase()) ||
          item.plateNo.toLowerCase().includes(q.toLowerCase()) ||
          item.aptNo.toLowerCase().includes(q.toLowerCase()) ||
          item.relationDegree.toLowerCase().includes(q.toLowerCase())
      );
      if (filtered.length > 0) {
        currentViewedRef.current = filtered[0].id;
        setSearchModal({
          open: true,
          results: filtered,
          query: q,
          subtitle: `"${q}" araması ile eşleşen ${filtered.length} kayıt`,
        });
      } else {
        notify("Bulunamadı", `"${q}" için bir kayıt bulunamadı.`, "info", false);
      }
    },
    [records, searchQuery, notify]
  );

  const openPersonCard = (id) => {
    const record = records.find((r) => r.id === id);
    if (record) {
      currentViewedRef.current = record.id;
      setSearchModal({ open: true, results: [record], query: record.fullName, subtitle: "Kişi Detayları" });
    }
  };

  const closeSearchModal = useCallback(() => {
    setSearchModal((m) => ({ ...m, open: false }));
    setSearchQuery("");
    waitingNoteRef.current = false;
    setNoteListeningId(null);
  }, []);

  const saveCardNote = useCallback(
    (id, note) => {
      persistRecords(records.map((r) => (r.id === id ? { ...r, infoNote: note } : r)));
      closeSearchModal();
      notify("Başarılı", "Kişi notu başarıyla güncellendi.", "success", true);
    },
    [records, persistRecords, notify, closeSearchModal]
  );

  // ---------- Logo ----------

  const handleLogoUpload = (dataUrl) => {
    setLogo(dataUrl);
    localStorage.setItem(LS.logo, dataUrl);
    notify("Başarılı", "Görsel başarıyla yüklendi.", "success", false);
  };

  // ---------- Sesli Komut ----------

  const disableVoice = useCallback(() => {
    setVoiceEnabled(false);
    notify("Bilgi", "Sesli komut devre dışı bırakıldı.", "info", false);
  }, [notify]);

  const toggleVoice = () => {
    if (!voiceSupported) {
      notify("Hata", "Tarayıcınız sesli komut özelliğini desteklemiyor.", "error", false);
      return;
    }
    if (voiceEnabled) disableVoice();
    else {
      setVoiceEnabled(true);
      notify("Başarılı", "Sesli komut aktifleştirildi.", "success", false);
    }
  };

  const handleVoiceResult = (speech) => {
    const lower = speech.toLowerCase();
    const s = stateRef.current;
    if (!s.isAuthenticated || !voiceEnabledRef.current) return;

    // Not dikte modu
    if (waitingNoteRef.current) {
      const noteContent = speech.charAt(0).toUpperCase() + speech.slice(1);
      const rec = records.find((r) => r.id === currentViewedRef.current);
      if (rec) {
        persistRecords(
          records.map((r) =>
            r.id === rec.id ? { ...r, infoNote: (r.infoNote ? r.infoNote + " | " : "") + noteContent } : r
          )
        );
        waitingNoteRef.current = false;
        setNoteListeningId(null);
        closeSearchModal();
        notify("Not Eklendi", "Söyledikleriniz kişi kartına başarıyla eklendi.", "success", true);
      }
      return;
    }

    if ((lower === "sil" || lower === "kaydı sil" || lower === "bunu sil") && s.searchOpen && currentViewedRef.current) {
      deleteRecord(currentViewedRef.current, true);
      return;
    }

    if (lower.includes("ses komutunu kapat") || lower === "sesi kapat") {
      disableVoice();
      return;
    }

    if (lower === "bilgi yaz" || lower === "bilgi ekle") {
      if (s.searchOpen && currentViewedRef.current) {
        waitingNoteRef.current = true;
        setNoteListeningId(currentViewedRef.current);
      } else if (s.view === "form") {
        setActiveField("infoNote");
      }
      return;
    }

    if (lower.includes("ayarlar") || lower.includes("ayarları aç")) {
      switchView("settings");
      return;
    }

    if (lower === "tamam" || lower === "kapat" || lower === "onayla") {
      if (s.customOpen) {
        handleCustomConfirm();
        return;
      }
      if (s.searchOpen) {
        closeSearchModal();
        return;
      }
    }

    if (s.customOpen && s.customIsConfirm) {
      if (lower.includes("hepsini sil") || lower.includes("evet") || lower.includes("sil")) {
        handleCustomConfirm();
        return;
      }
      if (lower.includes("vazgeç") || lower.includes("iptal")) {
        closeCustomModal();
        return;
      }
    }

    if (lower.includes("hepsini sil") || lower.includes("tümünü sil")) {
      confirmDeleteAll();
      return;
    }

    if (lower.startsWith("arama ")) {
      const q = speech.substring(6).trim();
      setSearchQuery(q);
      executeGlobalSearch(q);
      return;
    }

    if (lower.includes("yeni kayıt")) {
      switchView("form");
      return;
    }
    if (lower.includes("kayıtlı sakin") || lower.includes("kayıtlı kişiler") || lower.includes("kayıtlı plaka")) {
      switchView("list");
      return;
    }
    if (lower.includes("geri dön") || lower.includes("anasayfa")) {
      switchView("home");
      return;
    }

    if (
      lower.includes("kaydı tamamla") ||
      lower.includes("kaydet") ||
      lower.includes("şifreyi güncelle") ||
      lower.includes("şifre oluştur")
    ) {
      if (s.view === "form") {
        if (formApiRef.current) formApiRef.current.submit();
      } else if (s.view === "settings") {
        if (settingsApiRef.current) settingsApiRef.current.submitVisible();
      } else if (activeFieldRef.current && activeFieldRef.current.startsWith("noteInput_")) {
        if (searchModalApiRef.current) searchModalApiRef.current.saveNoteByField(activeFieldRef.current);
      }
      return;
    }

    // Anasayfa / liste görünümünde arama kutusuna dikte
    if (activeFieldRef.current === "globalSearchInput" && (s.view === "home" || s.view === "list")) {
      if (!lower.startsWith("sil") && !lower.includes("ileri") && lower !== "sil") {
        setSearchQuery(speech);
        executeGlobalSearch(speech);
        return;
      }
    }

    // Aktif alanı temizle
    if (lower.startsWith("sil") || lower === "sil" || lower === "temizle") {
      const id = activeFieldRef.current;
      if (id === "globalSearchInput") setSearchQuery("");
      else if (id && fieldSettersRef.current[id]) fieldSettersRef.current[id]("");
      return;
    }

    // Sıradaki alana geç
    if (lower === "ileri" || lower === "sonraki") {
      if (s.view === "form") {
        const idx = formFieldsOrder.indexOf(activeFieldRef.current);
        if (idx !== -1 && idx < formFieldsOrder.length - 1) setActiveField(formFieldsOrder[idx + 1]);
        else if (idx === formFieldsOrder.length - 1) {
          const btn = document.getElementById("btnSaveRecord");
          if (btn) btn.focus();
        }
      }
      return;
    }

    // Aktif alana dikte (form görünümü veya not alanları)
    const id = activeFieldRef.current;
    if (!id || id === "globalSearchInput") return;
    const isTextarea = id === "infoNote" || id.startsWith("noteInput_");
    if (s.view !== "form" && !isTextarea) return;
    const setter = fieldSettersRef.current[id];
    if (!setter) return;

    if (isTextarea) {
      const v = speech.charAt(0).toUpperCase() + speech.slice(1);
      setter(v, { append: true });
    } else {
      const v = id === "plateNo" ? speech.toUpperCase() : speech.charAt(0).toUpperCase() + speech.slice(1);
      setter(v);
      const idx = formFieldsOrder.indexOf(id);
      if (s.view === "form" && idx !== -1 && idx < formFieldsOrder.length - 1) {
        setActiveField(formFieldsOrder[idx + 1]);
      }
    }
  };

  const voiceEnabledRef = useRef(voiceEnabled);
  voiceEnabledRef.current = voiceEnabled;

  useVoiceEngine({
    enabled: voiceEnabled && isAuthenticated && voiceSupported,
    onResult: handleVoiceResult,
    onUnsupported: () => {
      setVoiceSupported(false);
      setVoiceEnabled(false);
    },
  });

  const voiceState = !voiceSupported ? "unsupported" : voiceEnabled ? "on" : "off";

  // ---------- Render ----------

  return (
    <div className="h-full bg-slate-900 text-slate-100 selection:bg-brand-500 selection:text-white relative overflow-hidden">
      {authView && (
        <AuthScreen
          authView={authView}
          onSwitchView={setAuthView}
          onLogin={handleLogin}
          verifyPhone={verifyPhone}
          onPasswordReset={handlePasswordReset}
          notify={notify}
        />
      )}

      <div className={isAuthenticated ? "h-full flex flex-col justify-between overflow-hidden" : "hidden"}>
        <Header
          logo={logo}
          onLogoUpload={handleLogoUpload}
          voiceState={voiceState}
          onToggleVoice={toggleVoice}
          onOpenSettings={() => switchView("settings")}
        />

        <main className="flex-1 max-w-md w-full mx-auto p-4 overflow-y-auto pb-32">
          {view === "home" && (
            <HomeView
              onNewRecord={() => switchView("form")}
              onOpenList={() => switchView("list")}
              plakaCount={records.length}
              sakinCount={records.length}
            />
          )}
          {view === "form" && (
            <FormView
              activeField={activeField}
              setActiveField={setActiveField}
              onSave={saveRecord}
              onBack={() => switchView("home")}
              registerSetters={registerSetters}
              apiRef={formApiRef}
            />
          )}
          {view === "list" && (
            <ListView
              records={records}
              onBack={() => switchView("home")}
              onDeleteAll={confirmDeleteAll}
              onDelete={(id) => deleteRecord(id, false)}
              onOpenCard={openPersonCard}
              onCopy={copyToClipboard}
            />
          )}
          {view === "settings" && (
            <SettingsView
              hasPassword={!!password}
              onBack={() => switchView("home")}
              onApplyTheme={(name) => applyTheme(name, true)}
              onCreatePassword={handleCreatePassword}
              onChangePassword={handleChangePassword}
              verifyOldPassword={verifyOldPassword}
              notify={notify}
              apiRef={settingsApiRef}
            />
          )}
        </main>

        <BottomSearch
          value={searchQuery}
          onChange={setSearchQuery}
          onSearch={() => executeGlobalSearch()}
          activeField={activeField}
          setActiveField={setActiveField}
        />

        <Footer animKey={footerKey} />
      </div>

      <SearchResultModal
        ref={searchModalApiRef}
        modal={searchModal}
        noteListeningId={noteListeningId}
        registerSetters={registerSetters}
        setActiveField={setActiveField}
        onClose={closeSearchModal}
        onDelete={(id) => deleteRecord(id, true)}
        onCopy={copyToClipboard}
        onNoteSave={saveCardNote}
      />

      <CustomModal modal={customModal} onConfirm={handleCustomConfirm} />
    </div>
  );
}
